import dayjs from "dayjs";
import {
  AlignmentType,
  BorderStyle,
  convertInchesToTwip,
  Document,
  HorizontalPositionRelativeFrom,
  ImageRun,
  Packer,
  PageBreak,
  Paragraph,
  ShadingType,
  Table,
  TableCell,
  TableRow,
  TextRun,
  TextWrappingType,
  VerticalPositionRelativeFrom,
  WidthType,
} from "docx";
import ExcelJS from "exceljs";
import {
  loadMockPreviewData,
  buildOrgNodeMaps,
  buildSelectedRelationshipTree,
  renderStructureNodes,
  getEntityLevelMeta,
} from "./loadPreviewData.js";
import { getFrameworkMeta } from "@/features/reporting-boundary/constants/framework-metadata";

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const sanitizeFileSegment = (value) =>
  String(value || "")
    .replace(/[\\/:*?"<>|]/g, "")
    .replace(/\s+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "");

const resolveMonthToken = (value) => {
  const text = String(value || "").trim();
  return /^\d{4}-(0[1-9]|1[0-2])$/.test(text)
    ? text
    : new Date().toISOString().slice(0, 7);
};

const buildPreviewReportExportBaseName = (report = {}) => {
  const reportName = sanitizeFileSegment(report?.name || "preview_report");
  const monthToken = resolveMonthToken(report?.endDate || report?.startDate);
  return `ESG_Reporting_${reportName}_${monthToken}`;
};

const buildPreviewReportExportFileName = (report = {}, extension = "") => {
  const baseName = buildPreviewReportExportBaseName(report);
  const normalizedExtension = String(extension || "").replace(/^\./, "");
  return normalizedExtension ? `${baseName}.${normalizedExtension}` : baseName;
};

const buildExcelSheetName = (value, fallback = "Sheet") =>
  String(value || fallback)
    .replace(/[\\/?*[\]:]/g, " ")
    .trim()
    .slice(0, 31) || fallback;

const getResolvedPreviewRows = (data) =>
  Array.isArray(data?.preview?.preview?.table?.rows)
    ? data.preview.preview.table.rows
    : [];

const getResolvedFrameworkCodes = (report = {}) =>
  Array.isArray(report?.frameworks)
    ? report.frameworks
    : Array.isArray(report?.frameworkCodes)
      ? report.frameworkCodes
      : [];

// Prefers the org's actual assigned-framework name (same source the
// Frameworks filter and Saved Reports list resolve against) so exports stay
// consistent with what's shown in the app; falls back to the static
// metadata table, then the raw code, for frameworks it doesn't recognize.
const resolveFrameworkName = (code, frameworkNameByCode) =>
  frameworkNameByCode?.get?.(code) || getFrameworkMeta(code).shortName || code;

const formatDateRange = (startDate, endDate) => {
  const start = dayjs(startDate);
  const end = dayjs(endDate);
  if (!start.isValid() || !end.isValid()) return "-";
  return `${start.format("MMM YYYY")} - ${end.format("MMM YYYY")}`;
};

const resolveReportExportPayload = async (report) => {
  const data = await loadMockPreviewData(report);
  const resolvedReport = data?.preview?.report || report;
  return {
    report: resolvedReport,
    data,
  };
};

const fetchImageBuffer = async (path) => {
  try {
    const res = await fetch(path);
    if (!res.ok) return null;
    return res.arrayBuffer();
  } catch {
    return null;
  }
};

// ─── DOCX color palette (mirrors PDF) ────────────────────────────────────────

const DC = {
  navy: "0F172A",
  slate: "334155",
  slateLight: "475569",
  muted: "64748B",
  border: "CBD5E1",
  borderLight: "E2E8F0",
  lightBg: "F1F5F9",
  rowAlt: "F8FAFC",
  accent: "059669",
  accentBorder: "10B981",
  white: "FFFFFF",
};

// ─── DOCX primitive helpers ───────────────────────────────────────────────────

const NO_BORDER = { style: BorderStyle.NONE, size: 0, color: "FFFFFF" };
const THIN = { style: BorderStyle.SINGLE, size: 2, color: DC.borderLight };

const docRun = (text, opts = {}) =>
  new TextRun({ text: String(text ?? ""), font: "Calibri", ...opts });

const docPageBreak = () =>
  new Paragraph({ children: [new PageBreak()], spacing: { after: 0 } });

const docDivider = () =>
  new Paragraph({
    text: "",
    border: {
      bottom: { style: BorderStyle.SINGLE, size: 4, color: DC.border },
    },
    spacing: { before: 80, after: 200 },
  });

const docSecHeading = (num, label) =>
  new Paragraph({
    children: [
      docRun(`${num}.  `, { size: 24, bold: true, color: DC.accent }),
      docRun(label, { size: 24, bold: true, color: DC.navy }),
    ],
    spacing: { before: 0, after: 280 },
    border: {
      bottom: { style: BorderStyle.SINGLE, size: 8, color: DC.accentBorder },
    },
  });

const docTh = (text, widthDxa) =>
  new TableCell({
    children: [
      new Paragraph({
        children: [
          docRun(text, {
            size: 17,
            bold: true,
            color: DC.muted,
            allCaps: true,
          }),
        ],
        spacing: { before: 100, after: 100 },
      }),
    ],
    shading: { type: ShadingType.CLEAR, fill: DC.lightBg },
    ...(widthDxa ? { width: { size: widthDxa, type: WidthType.DXA } } : {}),
    margins: { top: 80, bottom: 80, left: 160, right: 160 },
    borders: {
      top: THIN,
      bottom: { style: BorderStyle.SINGLE, size: 6, color: DC.border },
      left: THIN,
      right: THIN,
    },
  });

const docTd = (text, bold = false, alt = false) =>
  new TableCell({
    children: [
      new Paragraph({
        children: [
          docRun(String(text ?? "-"), {
            size: 18,
            bold,
            color: bold ? DC.navy : DC.slate,
          }),
        ],
        spacing: { before: 60, after: 60 },
      }),
    ],
    ...(alt ? { shading: { type: ShadingType.CLEAR, fill: DC.rowAlt } } : {}),
    margins: { top: 80, bottom: 80, left: 160, right: 160 },
    borders: { top: THIN, bottom: THIN, left: THIN, right: THIN },
  });

const docTable = (headerCells, dataRows) =>
  new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    rows: [
      new TableRow({ children: headerCells }),
      ...dataRows.map((cells) => new TableRow({ children: cells })),
    ],
  });

// ─── Document builder ─────────────────────────────────────────────────────────

const buildDocxDocument = async ({ report, data, t, frameworkNameByCode }) => {
  const [coverBuffer, logoBuffer] = await Promise.all([
    fetchImageBuffer("/images/report_cover.png"),
    fetchImageBuffer("/images/nxstart_logo.png"),
  ]);

  const includeCoverPage = report?.includeCoverPage !== false;
  const includeContentPage = report?.includeContentPage !== false;
  const reportingPeriod = formatDateRange(report?.startDate, report?.endDate);
  const preparedBy =
    report?.preparedBy ||
    report?.createdByName ||
    report?.updatedByName ||
    "NXStart ESG Team";
  const presentedTo = report?.presentedTo || "-";
  const year = dayjs().format("YYYY");
  const generatedDate = dayjs().format("DD MMM YYYY");
  const aboutText =
    report?.description ||
    t(
      "This ESG Report presents the organization's commitment to sustainability, responsible operations, and long-term value creation. It outlines key achievements and progress across Environmental, Social, and Governance priorities. The report reflects a strong focus on transparency, accountability, and delivering positive impact for stakeholders and the communities served.",
    );

  const orgMaps = buildOrgNodeMaps(data?.organizationTree || []);
  const organizationStructure = buildSelectedRelationshipTree({
    selected: report?.companies || [],
    buSide: report?.buSide,
    includeAllEntities: Boolean(report?.includeAllEntities),
    maps: orgMaps,
    t,
  });

  const allRows = Array.isArray(data?.preview?.preview?.table?.rows)
    ? data.preview.preview.table.rows
    : [];
  const consolidated = Boolean(data?.preview?.preview?.table?.consolidated);
  const PILLAR_ORDER = ["General", "Environmental", "Social", "Governance"];
  const pillars = [
    ...new Set(allRows.map((r) => r.pillar).filter(Boolean)),
  ].sort((a, b) => {
    const ai = PILLAR_ORDER.indexOf(a);
    const bi = PILLAR_ORDER.indexOf(b);
    return (
      (ai === -1 ? PILLAR_ORDER.length : ai) -
      (bi === -1 ? PILLAR_ORDER.length : bi)
    );
  });
  const selectedFrameworkCodes = getResolvedFrameworkCodes(report);

  const flatOrg = [];
  const walkOrg = (nodes, depth = 0) => {
    nodes.forEach((node) => {
      flatOrg.push({
        level: depth + 1,
        type: node.level || "-",
        title: node.title || "-",
        id: node.id,
      });
      if (node.children?.length) walkOrg(node.children, depth + 1);
    });
  };
  walkOrg(organizationStructure);

  // ── Cover Page ────────────────────────────────────────────────────────────

  const noBorderCell = (children, widthDxa) =>
    new TableCell({
      children,
      ...(widthDxa ? { width: { size: widthDxa, type: WidthType.DXA } } : {}),
      borders: {
        top: NO_BORDER,
        bottom: NO_BORDER,
        left: NO_BORDER,
        right: NO_BORDER,
      },
    });

  const coverChildren = includeCoverPage
    ? [
        // Full-page background image (floating, behind all text)
        ...(coverBuffer
          ? [
              new Paragraph({
                children: [
                  new ImageRun({
                    type: "png",
                    data: coverBuffer,
                    transformation: { width: 794, height: 1122 },
                    floating: {
                      horizontalPosition: {
                        relative: HorizontalPositionRelativeFrom.PAGE,
                        offset: 0,
                      },
                      verticalPosition: {
                        relative: VerticalPositionRelativeFrom.PAGE,
                        offset: 0,
                      },
                      allowOverlap: true,
                      behindDocument: true,
                      wrap: { type: TextWrappingType.NONE },
                    },
                  }),
                ],
                spacing: { after: 0 },
              }),
            ]
          : []),

        // Logo — top right
        new Paragraph({
          children: logoBuffer
            ? [
                new ImageRun({
                  type: "png",
                  data: logoBuffer,
                  transformation: { width: 150, height: 49 },
                }),
              ]
            : [
                docRun("NXStart", {
                  size: 20,
                  bold: true,
                  color: DC.slateLight,
                }),
              ],
          alignment: AlignmentType.RIGHT,
          spacing: { after: 0 },
        }),

        // Large spacer before main content
        new Paragraph({ text: "", spacing: { after: 720 } }),

        // Year — large ghost number (matches PDF year badge)
        new Paragraph({
          children: [docRun(year, { size: 128, bold: true, color: DC.navy })],
          spacing: { after: 0 },
        }),
        new Paragraph({
          children: [
            docRun(t("Sustainability Report"), {
              size: 18,
              bold: true,
              color: DC.slate,
              allCaps: true,
              characterSpacing: 100,
            }),
          ],
          spacing: { after: 480 },
        }),

        // "ESG REPORTING" eyebrow (right-aligned, matches PDF)
        new Paragraph({
          children: [
            docRun(t("ESG Reporting").toUpperCase(), {
              size: 17,
              bold: true,
              color: DC.slateLight,
              allCaps: true,
              characterSpacing: 120,
            }),
          ],
          alignment: AlignmentType.RIGHT,
          spacing: { after: 80 },
        }),

        // Report name — large, right-aligned (matches PDF right-side title)
        new Paragraph({
          children: [
            docRun(report?.name || t("ESG Report"), {
              size: 64,
              bold: true,
              color: DC.navy,
            }),
          ],
          alignment: AlignmentType.RIGHT,
          spacing: { after: 120 },
          border: {
            right: {
              style: BorderStyle.SINGLE,
              size: 16,
              color: DC.muted,
            },
          },
        }),

        // Reporting period (right-aligned, matches PDF)
        new Paragraph({
          children: [
            docRun(`${t("Reporting Period")}: `, {
              size: 20,
              color: DC.slate,
            }),
            docRun(reportingPeriod, {
              size: 20,
              bold: true,
              color: DC.slate,
            }),
          ],
          alignment: AlignmentType.RIGHT,
          spacing: { after: 480 },
        }),

        docDivider(),

        // Bottom metadata grid — 3 columns: Prepared By | Presented To | Generated
        // (matches PDF bottom section)
        new Table({
          width: { size: 100, type: WidthType.PERCENTAGE },
          borders: {
            top: NO_BORDER,
            bottom: NO_BORDER,
            left: NO_BORDER,
            right: NO_BORDER,
            insideH: NO_BORDER,
            insideV: NO_BORDER,
          },
          rows: [
            new TableRow({
              children: [
                noBorderCell([
                  new Paragraph({
                    children: [
                      docRun(t("Prepared By"), {
                        size: 16,
                        allCaps: true,
                        color: DC.slateLight,
                        characterSpacing: 60,
                      }),
                    ],
                    spacing: { after: 60 },
                  }),
                  new Paragraph({
                    children: [
                      docRun(preparedBy, {
                        size: 20,
                        bold: true,
                        color: DC.navy,
                      }),
                    ],
                  }),
                ]),
                noBorderCell([
                  new Paragraph({
                    children: [
                      docRun(t("Presented To"), {
                        size: 16,
                        allCaps: true,
                        color: DC.slateLight,
                        characterSpacing: 60,
                      }),
                    ],
                    spacing: { after: 60 },
                  }),
                  new Paragraph({
                    children: [
                      docRun(presentedTo, {
                        size: 20,
                        bold: true,
                        color: DC.navy,
                      }),
                    ],
                  }),
                ]),
                noBorderCell([
                  new Paragraph({
                    children: [
                      docRun(t("Generated"), {
                        size: 16,
                        allCaps: true,
                        color: DC.slateLight,
                        characterSpacing: 60,
                      }),
                    ],
                    alignment: AlignmentType.RIGHT,
                    spacing: { after: 60 },
                  }),
                  new Paragraph({
                    children: [
                      docRun(generatedDate, {
                        size: 20,
                        bold: true,
                        color: DC.navy,
                      }),
                    ],
                    alignment: AlignmentType.RIGHT,
                  }),
                ]),
              ],
            }),
          ],
        }),

        docPageBreak(),
      ]
    : [];

  // ── Table of Contents ─────────────────────────────────────────────────────

  const tocItems = [
    t("About Report"),
    t("Company Location Data"),
    t("Framework"),
    ...pillars.map((p) => `${p} ${t("Data")}`),
  ];

  const tocChildren = includeContentPage
    ? [
        new Paragraph({
          children: [
            docRun(t("Table of Contents"), {
              size: 40,
              bold: true,
              color: DC.navy,
            }),
          ],
          spacing: { after: 160 },
        }),
        docDivider(),
        ...tocItems.map(
          (item, i) =>
            new Paragraph({
              children: [
                docRun(`${i + 1}.`, {
                  size: 20,
                  bold: true,
                  color: DC.accent,
                }),
                docRun(`  ${item}`, { size: 20, color: DC.slate }),
              ],
              spacing: { before: 140, after: 140 },
              border: {
                bottom: {
                  style: BorderStyle.SINGLE,
                  size: 2,
                  color: DC.borderLight,
                },
              },
            }),
        ),
        docPageBreak(),
      ]
    : [];

  // ── About Report ──────────────────────────────────────────────────────────

  const aboutChildren = [
    docSecHeading(1, t("About Report")),
    new Paragraph({
      children: [docRun(aboutText, { size: 22, color: DC.slate })],
      spacing: { after: 160, line: 360 },
    }),
    docPageBreak(),
  ];

  // ── Company Location Data ─────────────────────────────────────────────────

  const locationDataRows = flatOrg.length
    ? flatOrg.map((row, idx) => {
        const node = orgMaps.nodeById.get(String(row.id));
        const alt = idx % 2 === 1;
        return [
          docTd(row.title, true, alt),
          docTd(node?.regionLabel || node?.region || "-", false, alt),
          docTd(node?.countryLabel || node?.country || "-", false, alt),
          docTd(node?.address || "-", false, alt),
        ];
      })
    : [
        [
          docTd(t("No location data available.")),
          docTd(""),
          docTd(""),
          docTd(""),
        ],
      ];

  const locationChildren = [
    docSecHeading(2, t("Company Location Data")),
    docTable(
      [
        docTh(t("Entity")),
        docTh(t("Region"), 1800),
        docTh(t("Country"), 1800),
        docTh(t("Address")),
      ],
      locationDataRows,
    ),
    docPageBreak(),
  ];

  // ── Framework ─────────────────────────────────────────────────────────────

  const frameworkDataRows = selectedFrameworkCodes.length
    ? selectedFrameworkCodes.map((code, idx) => {
        const meta = getFrameworkMeta(code);
        return [
          docTd(
            resolveFrameworkName(code, frameworkNameByCode),
            true,
            idx % 2 === 1,
          ),
          docTd(meta.description || "-", false, idx % 2 === 1),
        ];
      })
    : [
        [
          docTd(t("All frameworks included — no framework filter applied.")),
          docTd(""),
        ],
      ];

  const frameworkChildren = [
    docSecHeading(3, t("Framework")),
    docTable(
      [docTh(t("Framework"), 2400), docTh(t("Description"))],
      frameworkDataRows,
    ),
    ...(pillars.length ? [docPageBreak()] : []),
  ];

  // ── Pillar Data Sections ──────────────────────────────────────────────────

  const pillarChildren = pillars.flatMap((pillar, i) => {
    const pillarRows = allRows.filter((r) => r.pillar === pillar);

    const headerCells = consolidated
      ? [
          docTh(t("Code"), 1200),
          docTh(t("Title")),
          docTh(t("ESG Category"), 1600),
          docTh(t("Value"), 1600),
        ]
      : [
          docTh(t("Code"), 1000),
          docTh(t("Title")),
          docTh(t("Owner")),
          docTh(t("Parent Level")),
          docTh(t("ESG Category"), 1200),
          docTh(t("Date"), 1400),
          docTh(t("Value"), 1400),
        ];

    const dataRows = pillarRows.length
      ? pillarRows.map((row, idx) => {
          const alt = idx % 2 === 1;
          return consolidated
            ? [
                docTd(row.code, true, alt),
                docTd(row.codeTitle, false, alt),
                docTd(row.pillar, false, alt),
                docTd(row.value, true, alt),
              ]
            : [
                docTd(row.code, true, alt),
                docTd(row.codeTitle, false, alt),
                docTd(row.owner, false, alt),
                docTd(row.parentLevel, false, alt),
                docTd(row.pillar, false, alt),
                docTd(row.date, false, alt),
                docTd(row.value, true, alt),
              ];
        })
      : [
          consolidated
            ? [docTd(t("No data available.")), docTd(""), docTd(""), docTd("")]
            : [
                docTd(t("No data available.")),
                docTd(""),
                docTd(""),
                docTd(""),
                docTd(""),
                docTd(""),
                docTd(""),
              ],
        ];

    return [
      docSecHeading(4 + i, `${pillar} ${t("Data")}`),
      docTable(headerCells, dataRows),
      ...(i < pillars.length - 1 ? [docPageBreak()] : []),
    ];
  });

  return new Document({
    creator: "NXStart ESG Platform",
    title: report?.name || "ESG Report",
    description: report?.description || "ESG Sustainability Report",
    styles: {
      default: {
        document: {
          run: { font: "Calibri", size: 22, color: DC.slate },
        },
      },
    },
    sections: [
      {
        properties: {
          page: {
            margin: {
              top: convertInchesToTwip(1),
              right: convertInchesToTwip(1),
              bottom: convertInchesToTwip(1),
              left: convertInchesToTwip(1),
            },
          },
        },
        children: [
          ...coverChildren,
          ...tocChildren,
          ...aboutChildren,
          ...locationChildren,
          ...frameworkChildren,
          ...pillarChildren,
        ],
      },
    ],
  });
};

export const exportPreviewReportToWord = async ({
  report,
  t,
  frameworkNameByCode,
}) => {
  const payload = await resolveReportExportPayload(report);
  const doc = await buildDocxDocument({
    report: payload.report,
    data: payload.data,
    t,
    frameworkNameByCode,
  });
  const blob = await Packer.toBlob(doc);
  const fileName = buildPreviewReportExportFileName(payload.report, "docx");
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
  return fileName;
};

const TH = `padding:10px 14px;border-bottom:1px solid #e2e8f0;text-align:left;font-size:9pt;font-weight:600;text-transform:uppercase;letter-spacing:0.15em;color:#64748b;background:#f8fafc;`;
const TD = `padding:10px 14px;border-bottom:1px solid #f1f5f9;color:#334155;font-size:10pt;vertical-align:top;`;
const TD_BOLD = `${TD}font-weight:600;color:#0f172a;`;

const buildPdfPrintHtml = ({ report, data, t, frameworkNameByCode }) => {
  const includeCoverPage = report?.includeCoverPage !== false;
  const includeContentPage = report?.includeContentPage !== false;
  const reportingPeriod = formatDateRange(report?.startDate, report?.endDate);
  const preparedBy =
    report?.preparedBy ||
    report?.createdByName ||
    report?.updatedByName ||
    "NXStart ESG Team";
  const presentedTo = report?.presentedTo || "JJ-Group";
  const year = dayjs().format("YYYY");
  const aboutText =
    report?.description ||
    t(
      "This ESG Report presents the organization's commitment to sustainability, responsible operations, and long-term value creation. It outlines key achievements and progress across Environmental, Social, and Governance priorities. The report reflects a strong focus on transparency, accountability, and delivering positive impact for stakeholders and the communities served.",
    );

  const orgMaps = buildOrgNodeMaps(data?.organizationTree || []);
  const organizationStructure = buildSelectedRelationshipTree({
    selected: report?.companies || [],
    buSide: report?.buSide,
    includeAllEntities: Boolean(report?.includeAllEntities),
    maps: orgMaps,
    t,
  });

  const flattenStructure = (nodes = []) => {
    const result = [];
    const walk = (list) =>
      list.forEach((n) => {
        result.push(n);
        if (n.children?.length) walk(n.children);
      });
    walk(nodes);
    return result;
  };
  const allLocationNodes = flattenStructure(organizationStructure);

  const allRows = Array.isArray(data?.preview?.preview?.table?.rows)
    ? data.preview.preview.table.rows
    : [];
  const consolidated = Boolean(data?.preview?.preview?.table?.consolidated);
  const PILLAR_ORDER = ["General", "Environmental", "Social", "Governance"];
  const pillars = [
    ...new Set(allRows.map((r) => r.pillar).filter(Boolean)),
  ].sort((a, b) => {
    const ai = PILLAR_ORDER.indexOf(a);
    const bi = PILLAR_ORDER.indexOf(b);
    return (
      (ai === -1 ? PILLAR_ORDER.length : ai) -
      (bi === -1 ? PILLAR_ORDER.length : bi)
    );
  });

  const tocItems = [
    t("About Report"),
    t("Organization Profile"),
    t("Company Location Data"),
    t("Framework"),
    ...pillars.map((p) => `${p} ${t("Data")}`),
  ];

  const sectionHeader = (num, label) => `
    <p style="font-size:9pt;font-weight:700;text-transform:uppercase;letter-spacing:0.24em;color:#64748b;margin-bottom:16px;padding-bottom:10px;border-bottom:2px solid #e2e8f0;">
      ${num}. ${escapeHtml(label)}
    </p>`;

  const coverHtml = `
    <div style="position:relative;min-height:100vh;overflow:hidden;page-break-after:always;">
      <img src="/images/report_cover.png" alt="" style="position:absolute;top:0;left:0;width:100%;height:100%;object-fit:cover;z-index:0;" />
      <div style="position:absolute;inset:0;background:linear-gradient(135deg,rgba(255,255,255,0.2),rgba(255,255,255,0.1),rgba(15,23,42,0.1));z-index:1;"></div>
      <div style="position:relative;z-index:2;min-height:100vh;padding:56px 64px;display:flex;flex-direction:column;justify-content:space-between;">
        <div style="display:flex;justify-content:flex-end;">
          <img src="/images/nxstart_logo.png" alt="NXStart" style="height:64px;object-fit:contain;" />
        </div>
        <div style="display:inline-block;background:rgba(255,255,255,0.4);border:1px solid rgba(255,255,255,0.4);border-radius:8px;padding:20px 24px;">
          <div style="font-size:80pt;font-weight:700;line-height:1;color:#0f172a;">${escapeHtml(year)}</div>
          <p style="margin-top:8px;font-size:9pt;font-weight:700;text-transform:uppercase;letter-spacing:0.38em;color:#334155;">${escapeHtml(t("Sustainability Report"))}</p>
        </div>
        <div>
          <div style="display:flex;justify-content:flex-end;margin-bottom:32px;">
            <div style="max-width:600px;border-right:4px solid rgba(51,65,85,0.4);padding-right:24px;text-align:right;">
              <p style="font-size:9pt;font-weight:700;text-transform:uppercase;letter-spacing:0.32em;color:#475569;">${escapeHtml(t("ESG Reporting"))}</p>
              <h1 style="margin-top:8px;font-size:36pt;font-weight:600;line-height:1.2;color:#0f172a;">${escapeHtml(report?.name || t("ESG Report Preview"))}</h1>
              <p style="margin-top:12px;font-size:10pt;color:#334155;">${escapeHtml(t("Reporting Period"))}: <strong>${escapeHtml(reportingPeriod)}</strong></p>
            </div>
          </div>
          <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:24px;border-top:1px solid rgba(148,163,184,0.4);padding-top:20px;">
            <div>
              <p style="font-size:8pt;text-transform:uppercase;letter-spacing:0.24em;color:#475569;">${escapeHtml(t("Prepared By"))}</p>
              <p style="margin-top:6px;font-size:10pt;font-weight:700;color:#0f172a;">${escapeHtml(preparedBy)}</p>
            </div>
            <div>
              <p style="font-size:8pt;text-transform:uppercase;letter-spacing:0.24em;color:#475569;">${escapeHtml(t("Presented To"))}</p>
              <p style="margin-top:6px;font-size:10pt;font-weight:700;color:#0f172a;">${escapeHtml(presentedTo)}</p>
            </div>
            <div style="text-align:right;">
              <p style="font-size:8pt;text-transform:uppercase;letter-spacing:0.24em;color:#475569;">${escapeHtml(t("Generated"))}</p>
              <p style="margin-top:6px;font-size:10pt;font-weight:700;color:#0f172a;">${escapeHtml(dayjs().format("DD MMM YYYY"))}</p>
            </div>
          </div>
        </div>
      </div>
    </div>`;

  const tocHtml = `
    <div style="padding:48px 64px;page-break-after:always;">
      <p style="font-size:9pt;text-transform:uppercase;letter-spacing:0.3em;color:#64748b;margin-bottom:8px;">${escapeHtml(t("Contents"))}</p>
      <ol style="list-style:none;padding:0;margin:24px 0 0;">
        ${tocItems
          .map(
            (item, i) => `
          <li style="border-bottom:1px dashed #cbd5e1;padding:12px 0;font-size:11pt;color:#0f172a;font-weight:500;">${i + 1}. ${escapeHtml(item)}</li>
        `,
          )
          .join("")}
      </ol>
    </div>`;

  const aboutHtml = `
    <div style="padding:48px 64px;">
      ${sectionHeader(1, t("About Report"))}
      <p style="font-size:11pt;line-height:1.8;color:#334155;text-indent:2em;">${escapeHtml(aboutText)}</p>
    </div>`;

  const orgHtml = `
    <div style="padding:48px 64px;page-break-before:always;">
      ${sectionHeader(2, t("Organization Profile"))}
      <div style="border:1px solid #e2e8f0;border-radius:8px;background:#fff;padding:24px;">
        ${
          organizationStructure?.length
            ? renderStructureNodes(organizationStructure)
            : `<p style="font-size:10pt;color:#94a3b8;">${escapeHtml(t("No organization relationship data available."))}</p>`
        }
      </div>
    </div>`;

  const locationHtml = `
    <div style="padding:48px 64px;page-break-before:always;">
      ${sectionHeader(3, t("Company Location Data"))}
      <table style="width:100%;border-collapse:collapse;">
        <thead>
          <tr>
            <th style="${TH}">${escapeHtml(t("Entity"))}</th>
            <th style="${TH}">${escapeHtml(t("Region"))}</th>
            <th style="${TH}">${escapeHtml(t("Country"))}</th>
            <th style="${TH}">${escapeHtml(t("Address"))}</th>
          </tr>
        </thead>
        <tbody>
          ${
            allLocationNodes.length
              ? allLocationNodes
                  .map((structNode) => {
                    const node = orgMaps.nodeById.get(String(structNode.id));
                    const meta = getEntityLevelMeta(structNode.level);
                    const region = node?.regionLabel || node?.region || "-";
                    const country = node?.countryLabel || node?.country || "-";
                    const address = node?.address || "-";
                    return `
              <tr>
                <td style="${TD_BOLD}">
                  <span style="display:inline-flex;align-items:center;justify-content:center;width:20px;height:20px;border-radius:999px;background:#fff;border:1px solid #e2e8f0;color:${meta.color};margin-right:8px;vertical-align:middle;">${meta.svg}</span>
                  ${escapeHtml(structNode.title)}
                </td>
                <td style="${TD}">${escapeHtml(region)}</td>
                <td style="${TD}">${escapeHtml(country)}</td>
                <td style="${TD}">${escapeHtml(address)}</td>
              </tr>`;
                  })
                  .join("")
              : `<tr><td colspan="4" style="${TD}color:#94a3b8;">${escapeHtml(t("No location data available."))}</td></tr>`
          }
        </tbody>
      </table>
    </div>`;

  const selectedFrameworkCodes = Array.isArray(report?.frameworks)
    ? report.frameworks
    : Array.isArray(report?.frameworkCodes)
      ? report.frameworkCodes
      : [];
  const frameworkHtml = `
    <div style="padding:48px 64px;">
      ${sectionHeader(4, t("Framework"))}
      ${
        selectedFrameworkCodes.length === 0
          ? `<p style="font-size:10pt;color:#64748b;">${escapeHtml(t("All frameworks included — no framework filter applied."))}</p>`
          : `<table style="width:100%;border-collapse:collapse;">
          <thead>
            <tr>
              <th style="${TH}width:160px;">${escapeHtml(t("Framework"))}</th>
              <th style="${TH}">${escapeHtml(t("Description"))}</th>
            </tr>
          </thead>
          <tbody>
            ${selectedFrameworkCodes
              .map((code) => {
                const meta = getFrameworkMeta(code);
                return `
              <tr>
                <td style="${TD_BOLD}">${escapeHtml(resolveFrameworkName(code, frameworkNameByCode))}</td>
                <td style="${TD}">${escapeHtml(meta.description || "-")}</td>
              </tr>`;
              })
              .join("")}
          </tbody>
        </table>`
      }
    </div>`;

  const pillarSections = pillars
    .map((pillar, i) => {
      const rows = allRows.filter((r) => r.pillar === pillar);
      const colsConsolidated = [
        t("Code"),
        t("Title"),
        t("ESG Category"),
        t("Result"),
      ];
      const colsDetail = [
        t("Code"),
        t("Title"),
        t("Owner"),
        t("Parent Level"),
        t("ESG Category"),
        t("Date"),
        t("Result"),
      ];
      const cols = consolidated ? colsConsolidated : colsDetail;
      const bodyRows = rows.map((row) => {
        const result = !row.value && row.remark ? "n.a" : row.value || "-";
        const cells = consolidated
          ? [row.code, row.codeTitle || "-", row.pillar || "-", result]
          : [
              row.code,
              row.codeTitle || "-",
              row.owner || "-",
              row.parentLevel || "-",
              row.pillar || "-",
              row.date || "-",
              result,
            ];
        return `<tr>${cells.map((c, ci) => `<td style="${ci === 0 || ci === cells.length - 1 ? TD_BOLD : TD}">${escapeHtml(c)}</td>`).join("")}</tr>`;
      });
      return `
      <div style="padding:48px 64px;page-break-before:always;">
        ${sectionHeader(5 + i, `${pillar} ${t("Data")}`)}
        ${
          rows.length
            ? `
          <table style="width:100%;border-collapse:collapse;">
            <thead><tr>${cols.map((c) => `<th style="${TH}">${escapeHtml(c)}</th>`).join("")}</tr></thead>
            <tbody>${bodyRows.join("")}</tbody>
          </table>`
            : `<p style="font-size:10pt;color:#94a3b8;font-style:italic;">${escapeHtml(t("No data available."))}</p>`
        }
      </div>`;
    })
    .join("");

  return `<!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8" />
      <title>${escapeHtml(report?.name || "ESG Report")}</title>
      <style>
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { font-family: Calibri, 'Segoe UI', Arial, sans-serif; color: #0f172a; font-size: 11pt; line-height: 1.5; }
        @media print {
          * { -webkit-print-color-adjust: exact !important; color-adjust: exact !important; print-color-adjust: exact !important; }
        }
      </style>
    </head>
    <body>
      ${includeCoverPage ? coverHtml : ""}
      ${includeContentPage ? tocHtml : ""}
      ${aboutHtml}
      ${orgHtml}
      ${locationHtml}
      ${frameworkHtml}
      ${pillarSections}
    </body>
    </html>`;
};

export const exportPreviewReportToPdf = async ({
  report,
  t,
  frameworkNameByCode,
}) => {
  const payload = await resolveReportExportPayload(report);
  const html = buildPdfPrintHtml({
    report: payload.report,
    data: payload.data,
    t,
    frameworkNameByCode,
  });

  const iframe = document.createElement("iframe");
  iframe.setAttribute(
    "style",
    "position: fixed; right: 0; bottom: 0; width: 0; height: 0; border: 0; visibility: hidden;",
  );
  document.body.appendChild(iframe);

  const printWindow = iframe.contentWindow;
  if (!printWindow) {
    document.body.removeChild(iframe);
    throw new Error("Unable to prepare PDF print frame.");
  }

  const cleanup = () => {
    setTimeout(() => {
      if (iframe.parentNode) {
        iframe.parentNode.removeChild(iframe);
      }
    }, 1000);
  };

  printWindow.document.open();
  printWindow.document.write(html);
  printWindow.document.close();
  printWindow.document.title = buildPreviewReportExportBaseName(payload.report);

  await new Promise((resolve) => {
    const triggerPrint = () => {
      try {
        printWindow.focus();
        printWindow.print();
      } finally {
        cleanup();
        resolve();
      }
    };

    if (printWindow.document.readyState === "complete") {
      setTimeout(triggerPrint, 250);
      return;
    }

    iframe.onload = () => setTimeout(triggerPrint, 250);
  });

  return buildPreviewReportExportFileName(payload.report, "pdf");
};

// ─── Excel color palette & helpers ───────────────────────────────────────────

const EX = {
  navy: "FF0F172A",
  slate: "FF334155",
  muted: "FF64748B",
  accent: "FF059669",
  accentLight: "FF10B981",
  rowAlt: "FFF8FAFC",
  borderLight: "FFE2E8F0",
  white: "FFFFFFFF",
};

const exStyleHeaderRow = (row) => {
  row.height = 26;
  row.eachCell({ includeEmpty: true }, (cell) => {
    cell.font = {
      name: "Calibri",
      bold: true,
      size: 10,
      color: { argb: EX.muted },
    };
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFF1F5F9" },
    };
    cell.alignment = { vertical: "middle", horizontal: "left" };
    cell.border = { bottom: { style: "medium", color: { argb: EX.accent } } };
  });
};

const exStyleDataRow = (row, alt = false) => {
  row.height = 18;
  row.eachCell({ includeEmpty: true }, (cell) => {
    cell.font = { name: "Calibri", size: 10, color: { argb: EX.slate } };
    if (alt) {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: EX.rowAlt },
      };
    }
    cell.border = {
      bottom: { style: "hair", color: { argb: EX.borderLight } },
    };
    cell.alignment = { vertical: "middle" };
  });
};

const exAddSheet = (sheet, headers, rows) => {
  const headerRow = sheet.addRow(headers);
  exStyleHeaderRow(headerRow);
  sheet.views = [{ state: "frozen", ySplit: 1 }];
  sheet.autoFilter = {
    from: { row: 1, column: 1 },
    to: { row: 1, column: headers.length },
  };
  rows.forEach((rowData, i) => {
    const row = sheet.addRow(rowData);
    exStyleDataRow(row, i % 2 === 1);
  });
};

export const exportPreviewReportToExcel = async ({
  report,
  t,
  frameworkNameByCode,
}) => {
  const payload = await resolveReportExportPayload(report);
  const resolvedReport = payload.report;
  const previewRows = getResolvedPreviewRows(payload.data);
  const consolidated = Boolean(
    payload.data?.preview?.preview?.table?.consolidated,
  );
  const orgMaps = buildOrgNodeMaps(payload.data?.organizationTree || []);
  const organizationStructure = buildSelectedRelationshipTree({
    selected: resolvedReport?.companies || [],
    buSide: resolvedReport?.buSide,
    includeAllEntities: Boolean(resolvedReport?.includeAllEntities),
    maps: orgMaps,
    t,
  });
  const preparedBy =
    resolvedReport?.preparedBy ||
    resolvedReport?.createdByName ||
    resolvedReport?.updatedByName ||
    "NXStart ESG Team";

  // Collect the IDs of nodes included in the report (from the filtered tree)
  const selectedNodeIds = new Set();
  const collectSelectedIds = (nodes) => {
    for (const node of nodes) {
      if (node.id != null) selectedNodeIds.add(String(node.id));
      if (node.children?.length) collectSelectedIds(node.children);
    }
  };
  collectSelectedIds(organizationStructure);

  // Walk the full organizationTree (includes parent company at root) so that
  // ancestorPath always contains the complete hierarchy — same as Hierarchy
  // column in the preview table. Only push nodes that are in the report.
  const flatOrg = [];
  const walkFlatOrg = (nodes, ancestorPath = []) => {
    nodes.forEach((node) => {
      const id = String(node.id ?? node.name ?? "");
      if (selectedNodeIds.has(id)) {
        flatOrg.push({
          title: node.name || "-",
          id: node.id,
          ancestorPath: [...ancestorPath],
        });
      }
      if (node.children?.length)
        walkFlatOrg(node.children, [...ancestorPath, node.name || "-"]);
    });
  };
  walkFlatOrg(payload.data?.organizationTree || []);

  const workbook = new ExcelJS.Workbook();
  workbook.creator = "NXStart ESG Platform";
  workbook.created = new Date();
  workbook.title = resolvedReport?.name || "ESG Report";

  // ── Overview ───────────────────────────────────────────────────────────────
  const overviewSheet = workbook.addWorksheet(
    buildExcelSheetName(t("Overview"), "Overview"),
  );
  overviewSheet.properties.tabColor = { argb: EX.accent };
  overviewSheet.columns = [{ width: 24 }, { width: 52 }];

  const titleRow = overviewSheet.addRow([
    resolvedReport?.name || t("ESG Report"),
  ]);
  overviewSheet.mergeCells(1, 1, 1, 2);
  titleRow.height = 40;
  titleRow.getCell(1).font = {
    name: "Calibri",
    bold: true,
    size: 16,
    color: { argb: EX.navy },
  };
  titleRow.getCell(1).alignment = { vertical: "middle" };

  const subtitleRow = overviewSheet.addRow([
    `${t("Reporting Period")}: ${formatDateRange(resolvedReport?.startDate, resolvedReport?.endDate)}`,
  ]);
  overviewSheet.mergeCells(2, 1, 2, 2);
  subtitleRow.height = 22;
  subtitleRow.getCell(1).font = {
    name: "Calibri",
    size: 11,
    color: { argb: EX.muted },
  };
  subtitleRow.getCell(1).alignment = { vertical: "middle" };

  const accentLineRow = overviewSheet.addRow([]);
  overviewSheet.mergeCells(3, 1, 3, 2);
  accentLineRow.height = 4;
  accentLineRow.getCell(1).fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: EX.accent },
  };

  const spacerRow = overviewSheet.addRow([]);
  spacerRow.height = 8;

  const overviewHeaderRow = overviewSheet.addRow([t("Field"), t("Value")]);
  exStyleHeaderRow(overviewHeaderRow);

  const summaryFields = [
    [t("Report Name"), resolvedReport?.name || "-"],
    [
      t("Description"),
      resolvedReport?.description ||
        t(
          "This ESG Report presents the organization's commitment to sustainability, responsible operations, and long-term value creation. It outlines key achievements and progress across Environmental, Social, and Governance priorities. The report reflects a strong focus on transparency, accountability, and delivering positive impact for stakeholders and the communities served.",
        ),
    ],
    [t("Prepared By"), preparedBy],
    [t("Presented To"), resolvedReport?.presentedTo || "-"],
    [
      t("Reporting Period"),
      formatDateRange(resolvedReport?.startDate, resolvedReport?.endDate),
    ],
    [t("Consolidation"), consolidated ? t("Yes") : t("No")],
    [
      t("Include All Entities"),
      resolvedReport?.includeAllEntities ? t("Yes") : t("No"),
    ],
    [t("Generated"), dayjs().format("DD MMM YYYY")],
  ];

  summaryFields.forEach(([field, value], i) => {
    const row = overviewSheet.addRow([field, value]);
    exStyleDataRow(row, i % 2 === 1);
    row.getCell(1).font = {
      name: "Calibri",
      size: 10,
      bold: true,
      color: { argb: EX.navy },
    };
    if (i === 1) {
      row.height = 72;
      row.getCell(2).alignment = { vertical: "top", wrapText: true };
    }
  });

  // ── Company Location Data ──────────────────────────────────────────────────
  const locationSheet = workbook.addWorksheet(
    buildExcelSheetName(t("Company Location Data"), "Locations"),
  );
  locationSheet.properties.tabColor = { argb: "FF3B82F6" };
  locationSheet.columns = [
    { width: 36 },
    { width: 52 },
    { width: 20 },
    { width: 20 },
    { width: 40 },
  ];

  const locationRows = flatOrg.length
    ? flatOrg.map((row) => {
        const node = orgMaps.nodeById.get(String(row.id));
        return [
          row.title,
          row.ancestorPath.length ? row.ancestorPath.join(" > ") : "-",
          node?.regionLabel || node?.region || "-",
          node?.countryLabel || node?.country || "-",
          node?.address || "-",
        ];
      })
    : [[t("No location data available."), "", "", "", ""]];

  exAddSheet(
    locationSheet,
    [
      t("Organization"),
      t("Upper-level"),
      t("Region"),
      t("Country"),
      t("Address"),
    ],
    locationRows,
  );

  // ── Framework ─────────────────────────────────────────────────────────────
  const frameworkSheet = workbook.addWorksheet(
    buildExcelSheetName(t("Framework"), "Frameworks"),
  );
  frameworkSheet.properties.tabColor = { argb: EX.accentLight };
  frameworkSheet.columns = [{ width: 20 }, { width: 60 }];

  const selectedFrameworkCodes = getResolvedFrameworkCodes(resolvedReport);
  const frameworkRows = selectedFrameworkCodes.length
    ? selectedFrameworkCodes.map((code) => {
        const meta = getFrameworkMeta(code);
        return [
          resolveFrameworkName(code, frameworkNameByCode),
          meta.description || "-",
        ];
      })
    : [[t("All frameworks"), t("No framework filter applied.")]];

  exAddSheet(frameworkSheet, [t("Framework"), t("Description")], frameworkRows);

  // ── Pillar Sheets ──────────────────────────────────────────────────────────
  const pillarGroups = new Map();
  previewRows.forEach((row) => {
    const key = row?.pillar || t("Data");
    if (!pillarGroups.has(key)) pillarGroups.set(key, []);
    pillarGroups.get(key).push(row);
  });

  const PILLAR_COLORS = {
    Environmental: "FF059669",
    Social: "FF3B82F6",
    Governance: "FF8B5CF6",
  };

  if (pillarGroups.size === 0) {
    const emptySheet = workbook.addWorksheet(
      buildExcelSheetName(t("Preview Data"), "Data"),
    );
    emptySheet.columns = [{ width: 40 }];
    exAddSheet(emptySheet, [t("Message")], [[t("No preview data available.")]]);
  } else {
    pillarGroups.forEach((rows, pillar) => {
      const sheet = workbook.addWorksheet(
        buildExcelSheetName(`${pillar} ${t("Data")}`, pillar),
      );
      sheet.properties.tabColor = {
        argb: PILLAR_COLORS[pillar] || EX.accentLight,
      };

      const buSide = resolvedReport?.buSide || "";
      const showDimension = Boolean(buSide) && buSide !== "parent_company";
      const dimensionLabelMap = {
        level_1: t("BU"),
        level_2: t("Company"),
        level_3: t("Site"),
        country: t("Country"),
      };
      const dimensionFieldMap = {
        level_1: "bu",
        level_2: "companyAncestor",
        level_3: "owner",
        country: "country",
      };
      const dimensionLabel = dimensionLabelMap[buSide] || t("Dimension");
      const dimensionField = dimensionFieldMap[buSide];

      // Returns [displayValue, displayUnit].
      // Uses explicit `unit` field first; falls back to parsing the combined
      // "1,234.5 kWh" string when `unit` is absent (e.g. older snapshots).
      const parseValueUnit = (value, unit) => {
        if (unit) {
          const v =
            value && value.endsWith(` ${unit}`)
              ? value.slice(0, -(unit.length + 1)) || "-"
              : value || "-";
          return [v, unit];
        }
        if (!value) return ["-", "-"];
        const lastSpace = value.lastIndexOf(" ");
        if (lastSpace !== -1) {
          const left = value.slice(0, lastSpace);
          const right = value.slice(lastSpace + 1);
          if (!isNaN(parseFloat(left.replace(/,/g, "")))) return [left, right];
        }
        return [value, "-"];
      };

      const crossRefStr = (row) =>
        Array.isArray(row.crossRefCodes) && row.crossRefCodes.length
          ? row.crossRefCodes.join(", ")
          : "-";

      const headers = consolidated
        ? [
            t("Code"),
            t("Cross-Ref"),
            t("Title"),
            t("ESG Category"),
            ...(showDimension ? [dimensionLabel] : []),
            t("Reporting Year"),
            t("Result"),
            t("Unit"),
          ]
        : [
            t("Code"),
            t("Cross-Ref"),
            t("Title"),
            t("ESG Category"),
            ...(showDimension ? [dimensionLabel] : []),
            t("Organization"),
            t("Reporting Year"),
            t("Date"),
            t("Result"),
            t("Unit"),
          ];

      const sheetRows = rows.map((row) => {
        const [valDisplay, unitDisplay] =
          !row.value && row.remark
            ? ["n.a", "-"]
            : parseValueUnit(row.value, row.unit);
        if (consolidated) {
          return [
            row.code || "-",
            crossRefStr(row),
            row.codeTitle || "-",
            row.pillar || "-",
            ...(showDimension ? [row.dimensionName || "-"] : []),
            row.yearLabel || row.year || "-",
            valDisplay,
            unitDisplay,
          ];
        }
        const hierarchy =
          Array.isArray(row.ancestorPath) && row.ancestorPath.length
            ? row.ancestorPath.join(" > ")
            : [row.parentLevel, row.owner].filter(Boolean).join(" > ") || "-";
        return [
          row.code || "-",
          crossRefStr(row),
          row.codeTitle || "-",
          row.pillar || "-",
          ...(showDimension ? [row[dimensionField] || "-"] : []),
          hierarchy,
          row.yearLabel || row.year || "-",
          row.date || "-",
          valDisplay,
          unitDisplay,
        ];
      });

      exAddSheet(sheet, headers, sheetRows);
      sheet.columns = consolidated
        ? showDimension
          ? [
              { width: 16 }, // Code
              { width: 14 }, // Cross-Ref
              { width: 40 }, // Code Title
              { width: 16 }, // Pillar
              { width: 20 }, // Dimension
              { width: 12 }, // Year
              { width: 16 }, // Value
              { width: 12 }, // Unit
            ]
          : [
              { width: 16 }, // Code
              { width: 14 }, // Cross-Ref
              { width: 40 }, // Code Title
              { width: 16 }, // Pillar
              { width: 12 }, // Year
              { width: 16 }, // Value
              { width: 12 }, // Unit
            ]
        : showDimension
          ? [
              { width: 14 }, // Code
              { width: 14 }, // Cross-Ref
              { width: 36 }, // Code Title
              { width: 14 }, // Pillar
              { width: 20 }, // Dimension
              { width: 50 }, // Hierarchy
              { width: 12 }, // Year
              { width: 14 }, // Date
              { width: 14 }, // Value
              { width: 12 }, // Unit
            ]
          : [
              { width: 14 }, // Code
              { width: 14 }, // Cross-Ref
              { width: 36 }, // Code Title
              { width: 14 }, // Pillar
              { width: 50 }, // Hierarchy
              { width: 12 }, // Year
              { width: 14 }, // Date
              { width: 14 }, // Value
              { width: 12 }, // Unit
            ];
    });
  }

  // ── Download ───────────────────────────────────────────────────────────────
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const fileName = buildPreviewReportExportFileName(resolvedReport, "xlsx");
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
  return fileName;
};
